/* eslint-disable max-len */
import React, {useCallback} from 'react';
import {Form} from 'react-final-form';
import {useIntl} from 'react-intl';

import ProductModel from 'coreClasses/product/ProductModel';
import {useConfiguration, useCustomerMgr, useProducts} from 'coreHooks';

import Button from 'brandComponents/Button';
import Typography from 'brandComponents/Typography';
import VerifyForm from 'brandComponents/VerifyForm';

import {ReturnStatusBar} from './../index';
import ProductLine from './Subcomponents/ProductLine';
import {getSelectedItemsLength, isExchangeAllowed, isReturnRequested} from './Subcomponents/ProductLine/Subcomponents/ReturnExchange/utils';
import SelectPaymentMethod from './Subcomponents/SelectPaymentMethod';
import {IStep1Props} from './IStep1';
import messages from './messages';

import returnCreateFormStyles from '../../ReturnAuthorizationCreateForm.module.scss';
import styles from './Step1.module.scss';

const Step1 = ({handleStep, returnOrder, returnReasons, exchangeReasons, step}: IStep1Props) => {
	const intl = useIntl();
	const CustomerMgr = useCustomerMgr();
	const customer = CustomerMgr.getCurrentCustomer();
	const isStoreCreditEnabled = useConfiguration('sitePreferences.StoreCreditEnable') && customer?.isRegistered;
	const productItems = returnOrder.getProductItems(true);

	const products = useProducts(productItems.map((item) => item.getProductId()) || []) as ProductModel[];

	const paymentInstruments = returnOrder.getPaymentInstruments().map((i) =>
		(!isStoreCreditEnabled || i.getPaymentMethodId() !== 'CASH_ON_DELIVERY') && ({
			id: i.getPaymentMethodId(),
			label: intl.formatMessage(i.isGiftCertificate() ? messages.giftCertificateLabel : messages.creditCardLabel),
		})
	);

	if (isStoreCreditEnabled && customer?.isRegistered) {
		paymentInstruments.push({
			id: 'STORE_CREDIT',
			label: intl.formatMessage(messages.storeCreditLabel),
		});
	}

	const onSubmit = (values) => {
		const isValid = isValidItems(values);
		const updatedReturnProducts = getUpdatesReturnProducts(values);

		return isValid.length ? isValid : handleStep && handleStep(updatedReturnProducts);
	};

	const isValidItems = useCallback((formValues) => {
		let errors = [];

		const getReturnReasonId = (key) => {
			const reasonId = `returnReason-${key.split('-')[1]}`;

			return reasonId;
		};

		const getExchangeReasonId = (key) => {
			const reasonId = `exchangeReason-${key.split('-')[1]}`;

			return reasonId;
		};

		Object.entries(formValues).map(([key, value]) => {
			if (key.startsWith('returnProduct-') && !!value ? !formValues[getReturnReasonId(key)] : false) {
				errors.push(key);
			} else if (key.startsWith('exchangeProduct-') && !!value ? !formValues[getExchangeReasonId(key)] : false) {
				errors.push(key);
			} else if (key.startsWith('exchangeItem-') && !isExchangeAllowed(returnOrder, formValues, key, value)) {
				errors.push(key);
			}
		});

		return errors;
	}, []);

	const getUpdatesReturnProducts = useCallback((values) => {
		const updatedSelectedItems = {};

		Object.entries(values).forEach(([key, value]) => {
			if (value) {
				updatedSelectedItems[key] = value;
			}
		});

		return updatedSelectedItems;
	}, []);

	return (
		returnOrder && (
			<>
				<Typography variant="h4" classNames={styles['return-create-step-1__title']}>
					{intl.formatMessage(messages.title)}
				</Typography>
				<ReturnStatusBar currentStep={0} />
				<Form
					onSubmit={onSubmit}
					render={({handleSubmit, submitting, values}) => {
						const numberOfSelectedItems = getSelectedItemsLength(values);
						const itemsError = isValidItems(values);

						let isDisabled: boolean = !numberOfSelectedItems || !!itemsError.length || submitting;

						return (
							<VerifyForm classNames={styles['return-create-step-1__form']} onSubmit={handleSubmit}>
								<Typography classNames={styles['return-create-step-1__description']} variant="p">
									{intl.formatMessage(messages.description)}
								</Typography>

								<ProductLine
									returnOrder={returnOrder}
									step={step}
									products={products}
									returnReasons={returnReasons}
									exchangeReasons={exchangeReasons}
								/>

								{isReturnRequested(values) && !isStoreCreditEnabled && (
									<div className={returnCreateFormStyles['return-create__form-payments']}>
										<SelectPaymentMethod paymentInstruments={paymentInstruments} values={values} />
									</div>
								)}

								<div className={returnCreateFormStyles['return-create__form-apply-wrapper']}>
									<Button
										classNames={returnCreateFormStyles['return-create__form-apply']}
										disabled={isDisabled}
										type="submit"
									>
										{intl.formatMessage(messages.continueButtonText, {
											number: numberOfSelectedItems,
										})}
									</Button>
								</div>
							</VerifyForm>
						);
					}}
				/>
			</>
		)
	);
};

export default Step1;
